// The Professional section — what a colleague sees. It surfaces the professional
// knowledge they're allowed (org-scoped), behind the org-aware twin, with a CTA
// to talk. The owner/trustee can preview it too.

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTwinName } from '../lib/twin.js';

interface Mem {
  id: string; title: string | null; body: string; themes: string[];
  category?: string; lifeChapter: string | null; approxYear: number | null;
}

export function Professional({ onConverse }: { onConverse: () => void }) {
  const { user } = useAuth();
  const twinName = useTwinName();
  const mineQ = useQuery<{ organization: { id: string; name: string } | null }>({
    queryKey: ['organizations', 'mine'],
    queryFn: () => api('/api/organizations/mine'),
  });
  const memsQ = useQuery<{ memories: Mem[] }>({
    queryKey: ['memory', 'list'],
    queryFn: () => api('/api/memory'),
  });

  const org = mineQ.data?.organization ?? null;
  const pro = (memsQ.data?.memories ?? []).filter((m) => m.category === 'professional');

  return (
    <div className="px-5 md:px-10 pb-12 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-5 pt-2">
        <div className="min-w-0">
          <div className="text-[11.5px] text-particle/80">
            Professional{org ? ` · ${org.name}` : ''}
          </div>
          <h1 className="font-serif text-3xl text-ink leading-tight mt-1">
            {twinName} <em className="italic text-particle">at work</em>
          </h1>
          <p className="font-serif italic text-ink/55 mt-1 max-w-prose">
            Ask {twinName} about Martin’s professional experience{org ? ` and ${org.name}-related context` : ''}. Everything here is scoped to what you’re allowed to see.
          </p>
          <button onClick={onConverse}
            className="mt-3 px-5 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle">
            Talk to {twinName} →
          </button>
        </div>
      </div>

      {/* Professional knowledge */}
      <div className="mt-8">
        <div className="text-[11.5px] text-ink/40 mb-3">
          Professional knowledge{pro.length ? ` · ${pro.length}` : ''}
        </div>
        {memsQ.isLoading ? (
          <div className="text-ink/40 text-[12px] py-6">Loading…</div>
        ) : pro.length === 0 ? (
          <p className="font-serif italic text-ink/45">
            Nothing here yet — but you can still ask {twinName} anything professional in the conversation.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {pro.map((m) => (
              <li key={m.id} className="rounded-xl border border-ink/[0.08] p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-serif text-[15px] text-ink">{m.title || (m.lifeChapter ?? 'Note')}</span>
                  {m.approxYear && <span className="text-[11px] text-ink/35">{m.approxYear}</span>}
                </div>
                <p className="font-serif text-[14px] text-ink/70 leading-relaxed line-clamp-3 whitespace-pre-wrap">
                  {m.body.length > 280 ? m.body.slice(0, 279) + '…' : m.body}
                </p>
                {m.themes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.themes.slice(0, 6).map((t) => (
                      <span key={t} className="text-[10px] text-ink/40 border border-ink/12 rounded-full px-1.5 py-0.5">{t}</span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {!org && user?.role !== 'owner' && user?.role !== 'trustee' && (
        <p className="mt-6 font-serif italic text-[13px] text-ink/40">
          You’re not linked to a company yet — ask the owner to add you to an organization for company-scoped context.
        </p>
      )}
    </div>
  );
}
